import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../../components/Spinner";
import { useGetDocTypeList } from "../../../shared/hooks/useDocTypesData";
import { useLoadDoctype } from "../../../shared/hooks/useFrappeDoctype";
import { PosContext } from "../../../shared/PosContext";
import {
    calculateMaterializedAddonTotal,
    flattenAddonTree,
    hydrateAddonTreeForCart,
} from "../../Selling/Widgets/AddItemWidget/addonTree";

const safeParseAddons = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getLineAmount = (item, addons) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || item.standard_rate || 0);
    return qty * rate + calculateMaterializedAddonTotal(addons, qty);
};

function OrderDetailsDialog({ isOpen, toggle, title, selectedCustomer, orderName }) {
    const { t } = useTranslation();

    const { getMenuItem, addonsItems, menuItems } = useContext(PosContext);

    const itemLookup = useMemo(() => {
        const lookup = new Map();
        [...(menuItems || []), ...(addonsItems || [])].forEach((candidate) => {
            const code = candidate?.item_code || candidate?.name;
            if (code) lookup.set(code, candidate);
        });
        return lookup;
    }, [menuItems, addonsItems]);

    const shouldFetchCustomerOrders = isOpen && !!selectedCustomer && !orderName;
    const {
        data: getCustomerOrders,
        isLoading: isCustomerOrdersLoading,
        isFetching: isCustomerOrdersFetching,
    } = useGetDocTypeList({
        doctype: shouldFetchCustomerOrders ? "Table Order" : null,
        filters: shouldFetchCustomerOrders
            ? [
                  ["customer", "=", selectedCustomer],
                  ["status", "=", "Invoiced"],
              ]
            : null,
        sortBy: "creation desc",
        limit: 20,
        fields: ["name", "creation"],
    });

    const resolvedOrderName = orderName || getCustomerOrders?.[0]?.name;
    const {
        data: getCustomerLastOrder,
        isLoading: isOrderLoading,
        isFetching: isOrderFetching,
    } = useLoadDoctype(
        "Table Order",
        resolvedOrderName
    );

    const invoiceData = useMemo(() => {
        return getCustomerLastOrder?.docs?.[0] || {};
    }, [getCustomerLastOrder]);

    const items = invoiceData.entry_items || [];

    const displayItems = useMemo(() => {
        return items.map((item, idx) => {
            const menuItem = getMenuItem?.(item.item_code) || {};
            const parsedAddons = safeParseAddons(item.addons);
            const itemAddonTree = hydrateAddonTreeForCart(
                parsedAddons,
                itemLookup
            );
            const itemAddons = flattenAddonTree(itemAddonTree);

            const displayItem = {
                ...item,
                item_name: item.item_name || menuItem.item_name || item.item_code,
                cover: menuItem.cover || menuItem.image || item.cover,
                standard_rate: item.standard_rate || menuItem.standard_rate,
            };

            return {
                key: item.name || item.identifier || `${item.item_code}-${idx}`,
                item: displayItem,
                addons: itemAddons,
                qty: Number(item.qty || 0),
                amount: getLineAmount(displayItem, itemAddonTree),
            };
        });
    }, [items, getMenuItem, itemLookup]);

    const total = displayItems.reduce((sum, item) => sum + item.amount, 0);
    const hasLoadedCustomerOrders =
        !shouldFetchCustomerOrders ||
        (!!getCustomerOrders && !isCustomerOrdersLoading);
    const hasNoOrder = hasLoadedCustomerOrders && !resolvedOrderName;
    const isLoading =
        isCustomerOrdersLoading ||
        isCustomerOrdersFetching ||
        (!!resolvedOrderName && (isOrderLoading || isOrderFetching));
    const showEmpty = !isLoading && (hasNoOrder || displayItems.length === 0);

    return (
        <Dialog
            open={isOpen}
            onClose={() => {
                toggle?.();
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 16,
                    maxHeight: "86vh",
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem 1.25rem",
                    borderBottom: "1px solid #e5e9f2",
                }}
            >
                <div style={{ fontWeight: 900, fontSize: 18 }}>
                    {title || t("pos.orderDetails")}
                </div>
                <Button
                    onClick={() => {
                        toggle();
                    }}
                    color="error"
                    style={{ fontWeight: 800 }}
                >
                    {t("common.close")}
                </Button>
            </DialogTitle>
            <DialogContent
                style={{
                    background: "#f5f6fa",
                    padding: "1rem",
                    minHeight: 260,
                }}
            >
                {isLoading ? (
                    <div
                        style={{
                            minHeight: 220,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Spinner />
                    </div>
                ) : showEmpty ? (
                    <div
                        style={{
                            minHeight: 220,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "#6E7187",
                            background: "#fff",
                            border: "1px dashed #d7dce8",
                            borderRadius: 14,
                            padding: "2rem",
                        }}
                    >
                        <div style={{ fontWeight: 900, color: "#271D1C" }}>
                            {t("pos.noPreviousOrders")}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 13 }}>
                            {t("pos.noPreviousOrdersHint")}
                        </div>
                    </div>
                ) : (
                    <div style={{ display: "grid", gap: "0.75rem" }}>
                        {displayItems.map(({ key, item, addons, qty, amount }) => (
                            <div
                                key={key}
                                style={{
                                    background: "#fff",
                                    border: "1px solid #e5e9f2",
                                    borderRadius: 14,
                                    padding: "0.75rem",
                                    boxShadow: "0 8px 22px rgba(39, 29, 28, 0.04)",
                                }}
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "56px 1fr auto",
                                        gap: "0.75rem",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 12,
                                            overflow: "hidden",
                                            background: "#f5f6fa",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 900,
                                            color: "#6E7187",
                                        }}
                                    >
                                        {item.cover ? (
                                            <img
                                                src={item.cover}
                                                alt={item.item_name || item.item_code}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        ) : (
                                            String(item.item_name || item.item_code || "?")
                                                .slice(0, 1)
                                                .toUpperCase()
                                        )}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontWeight: 900,
                                                color: "#271D1C",
                                                marginBottom: 4,
                                            }}
                                        >
                                            {item.item_name || item.item_code}
                                        </div>
                                        <div
                                            style={{
                                                color: "#6E7187",
                                                fontSize: 13,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {t("pos.quantity")}: {qty}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            fontWeight: 900,
                                            color: "#271D1C",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        NIS {amount}
                                    </div>
                                </div>

                                {addons.length ? (
                                    <div
                                        style={{
                                            marginTop: "0.75rem",
                                            paddingTop: "0.75rem",
                                            borderTop: "1px solid #eef1f6",
                                            display: "grid",
                                            gap: "0.35rem",
                                        }}
                                    >
                                        {addons.map(({ addon, depth }, addonIndex) => (
                                            <div
                                                key={`${key}-${addon.identifier || addon.item_code}-${addonIndex}`}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: "1rem",
                                                    color: "#6E7187",
                                                    fontSize: 12,
                                                    paddingInlineStart: depth * 14,
                                                }}
                                            >
                                                <span>
                                                    {depth > 0 ? "↳ " : "+ "}
                                                    {addon.item_name ||
                                                        addon.item_code}
                                                </span>
                                                <span>
                                                    NIS{" "}
                                                    {Number(
                                                        addon.standard_rate ||
                                                            addon.rate ||
                                                            0
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ))}

                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e5e9f2",
                                borderRadius: 14,
                                padding: "0.9rem 1rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontWeight: 900,
                                color: "#271D1C",
                            }}
                        >
                            <span>{t("pos.total")}</span>
                            <span>NIS {total}</span>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default OrderDetailsDialog;
